/**
 * AI Skills Service
 * 
 * Provides access to specialized AI capabilities across Quotech.
 * Each skill is a focused capability with specific inputs and outputs.
 */

import { getAuthCookie } from '@quotech/auth';

import { API_BASE } from '@quotech/api';
import { buildOrganisationHeader } from '../../organisation';

// =============================================================================
// Types
// =============================================================================

export interface AISkill {
  id: string;
  name: string;
  description: string;
  category: 'communication' | 'analysis' | 'prediction' | 'automation';
  icon: string; // Lucide icon name
  endpoint: string;
  requiresQuoteId?: boolean;
  requiresJobId?: boolean;
  available: boolean;
}

export interface EmailComposerInput {
  quoteId: number;
  emailType: 'initial' | 'followup' | 'revision' | 'reminder';
  recipientName?: string;
  recipientEmail?: string;
  customMessage?: string;
}

export interface EmailComposerOutput {
  subject: string;
  body: string;
  suggestedAttachments: string[];
  mergeFieldsUsed: string[];
}

export interface WinPredictionInput {
  quoteId: number;
}

export interface WinPredictionOutput {
  probability: number;
  confidence: number;
  positiveFactors: string[];
  negativeFactors: string[];
  recommendations: string[];
  similarQuotes: Array<{
    quoteNumber: string;
    won: boolean;
    probability: number;
  }>;
}

export interface FollowupSuggestion {
  quoteId: number;
  quoteNumber: string;
  customerName: string;
  daysOpen: number;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  suggestedAction: string;
  reason: string;
}

export interface JobAnalysisInput {
  jobId: number;
}

export interface JobAnalysisOutput {
  meta: {
    jobId: number;
    generatedAt: string;
    confidenceScore: number;
  };
  summary: {
    kpis: Record<string, number>;
    insights: string[];
  };
  moneyMoves: Array<{
    title: string;
    expectedImpact: number;
    timeToCashDays: number;
    ownerRole: string;
    confidence: number;
    why: string;
  }>;
  labour: {
    kpis: Record<string, number>;
    insights: string[];
    actions: string[];
  };
  materials: {
    kpis: Record<string, number>;
    insights: string[];
    actions: string[];
  };
  cashAr: {
    summary: {
      outstandingExGst: number;
      overdueExGst: number;
      dsoDays: number;
    };
    nextCash: Array<{
      invoiceNumber: string;
      dueDate: string;
      daysOverdue: number;
      amountExGst: number;
      action: string;
    }>;
  };
  dataWarnings: string[];
}

// =============================================================================
// Available Skills Registry
// =============================================================================

export const AI_SKILLS: AISkill[] = [
  {
    id: 'email-composer',
    name: 'Email Composer',
    description: 'Generate professional emails for quotes, follow-ups, and reminders',
    category: 'communication',
    icon: 'Mail',
    endpoint: '/api/ai/quote/{quoteId}/compose-email/',
    requiresQuoteId: true,
    available: true,
  },
  {
    id: 'win-prediction',
    name: 'Win Prediction',
    description: 'Predict quote win probability with factor analysis',
    category: 'prediction',
    icon: 'TrendingUp',
    endpoint: '/api/ai/quote/{quoteId}/win-prediction/',
    requiresQuoteId: true,
    available: true,
  },
  {
    id: 'followup-suggestions',
    name: 'Follow-up Suggestions',
    description: 'Get AI recommendations for quotes that need attention',
    category: 'automation',
    icon: 'Bell',
    endpoint: '/api/ai/followups/',
    available: true,
  },
  {
    id: 'job-analysis',
    name: 'Deep Job Analysis',
    description: 'Comprehensive job analysis with money moves and insights',
    category: 'analysis',
    icon: 'BarChart3',
    endpoint: '/api/ai/analysis/{jobId}/',
    requiresJobId: true,
    available: true,
  },
  {
    id: 'quote-suggestions',
    name: 'Quote Suggestions',
    description: 'AI suggestions for quote pricing and scope',
    category: 'analysis',
    icon: 'Lightbulb',
    endpoint: '/api/ai/quote/suggestions/',
    available: true,
  },
  {
    id: 'outlook-draft',
    name: 'Create Outlook Draft',
    description: 'Create email drafts directly in Outlook',
    category: 'communication',
    icon: 'Send',
    endpoint: '/api/ai/outlook/draft/',
    available: true,
  },
];

// =============================================================================
// API Functions
// =============================================================================

async function apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthCookie();
  
  // Check authentication first
  if (!token) {
    throw new Error('Authentication required. Please log in to use AI skills.');
  }
  
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        ...buildOrganisationHeader(),
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
    });
    
    if (response.status === 401) {
      throw new Error('Authentication expired. Please log in again.');
    }
    
    if (response.status === 403) {
      throw new Error('You do not have permission to access this skill.');
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || error.detail || `API Error: ${response.status}`);
    }
    
    return response.json();
  } catch (err: unknown) {
    // Handle network errors
    if (err.name === 'TypeError' && err.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw err;
  }
}

/**
 * Compose an email for a quote
 */
export async function composeEmail(input: EmailComposerInput): Promise<EmailComposerOutput> {
  const response = await apiCall<Record<string, unknown>>(`/api/ai/quote/${input.quoteId}/compose-email/`, {
    method: 'POST',
    body: JSON.stringify({
      email_type: input.emailType,
      recipient_name: input.recipientName,
      recipient_email: input.recipientEmail,
      custom_message: input.customMessage,
    }),
  });
  
  return {
    subject: response.subject,
    body: response.body,
    suggestedAttachments: response.suggested_attachments || [],
    mergeFieldsUsed: response.merge_fields_used || [],
  };
}

/**
 * Get win probability prediction for a quote
 */
export async function getWinPrediction(input: WinPredictionInput): Promise<WinPredictionOutput> {
  const response = await apiCall<Record<string, unknown>>(`/api/ai/quote/${input.quoteId}/win-prediction/`);
  
  return {
    probability: response.probability,
    confidence: response.confidence,
    positiveFactors: response.positive_factors || [],
    negativeFactors: response.negative_factors || [],
    recommendations: response.recommendations || [],
    similarQuotes: (response.similar_quotes || []).map((q: Record<string, unknown>) => ({
      quoteNumber: q.quote_number,
      won: q.won,
      probability: q.probability,
    })),
  };
}

/**
 * Get follow-up suggestions
 */
export async function getFollowupSuggestions(): Promise<FollowupSuggestion[]> {
  const response = await apiCall<Record<string, unknown>[]>('/api/ai/followups/');
  
  return response.map(item => ({
    quoteId: item.quote_id,
    quoteNumber: item.quote_number,
    customerName: item.customer_name,
    daysOpen: item.days_open,
    priority: item.priority,
    suggestedAction: item.suggested_action,
    reason: item.reason,
  }));
}

/**
 * Get deep job analysis
 */
export async function getJobAnalysis(input: JobAnalysisInput): Promise<JobAnalysisOutput> {
  const response = await apiCall<Record<string, unknown>>(`/api/report/job/${input.jobId}/ai-analysis/`, {
    method: 'POST',
  });
  
  // Map snake_case to camelCase
  return {
    meta: {
      jobId: response.meta?.job_id || input.jobId,
      generatedAt: response.meta?.generated_at || new Date().toISOString(),
      confidenceScore: response.meta?.confidence_score || 0,
    },
    summary: {
      kpis: response.summary?.kpis || {},
      insights: response.summary?.insights || [],
    },
    moneyMoves: (response.money_moves || []).map((m: Record<string, unknown>) => ({
      title: m.title,
      expectedImpact: m.expected_impact_$ || m.expected_impact,
      timeToCashDays: m.time_to_cash_days,
      ownerRole: m.owner_role,
      confidence: m.confidence,
      why: m.why,
    })),
    labour: {
      kpis: response.labour?.kpis || {},
      insights: response.labour?.insights || [],
      actions: response.labour?.actions || [],
    },
    materials: {
      kpis: response.materials?.kpis || {},
      insights: response.materials?.insights || [],
      actions: response.materials?.actions || [],
    },
    cashAr: {
      summary: {
        outstandingExGst: response.cash_ar?.summary?.outstanding_ex_gst || 0,
        overdueExGst: response.cash_ar?.summary?.overdue_ex_gst || 0,
        dsoDays: response.cash_ar?.summary?.dso_days || 0,
      },
      nextCash: (response.cash_ar?.next_cash || []).map((c: Record<string, unknown>) => ({
        invoiceNumber: c.invoice_number,
        dueDate: c.due_date,
        daysOverdue: c.days_overdue,
        amountExGst: c.amount_ex_gst,
        action: c.action,
      })),
    },
    dataWarnings: response.data_warnings || [],
  };
}

/**
 * Get skill by ID
 */
export function getSkillById(id: string): AISkill | undefined {
  return AI_SKILLS.find(skill => skill.id === id);
}

/**
 * Get skills by category
 */
export function getSkillsByCategory(category: AISkill['category']): AISkill[] {
  return AI_SKILLS.filter(skill => skill.category === category);
}

